import { createContext } from 'react';
import { createTheme } from '@mui/material';

// Тёмная тема (dark-only), стиль «cosmic command deck» (референс GitHub, DESIGN 3).
// Глубина - за счёт translucent glass-поверхностей и hairline-бордеров, без теней.
// Дисциплина: один функциональный акцент (terminal green), фиолетовый - только атмосфера/бордер.
export const ColorModeContext = createContext({ mode: 'dark', toggle: () => {} });

// Токены (см. knowledge/design-refs + DESIGN 3)
export const T = {
  canvas: '#0d1117',            // Deep Void - канвас страницы
  surface: '#151a22',           // Obsidian - инпуты, элевированные элементы
  glass: 'rgba(255,255,255,0.05)',        // Glass - заливка карточек
  glassHover: 'rgba(255,255,255,0.09)',
  border: '#21262d',            // Slate Edge - основной бордер (элевация)
  borderStrong: '#30363d',
  glassBorder: 'rgba(255,255,255,0.10)',  // бордер glass-карточек
  textPrimary: '#e6edf3',       // почти Snow, мягче чистого белого
  textSecondary: '#a4aea6',     // Pearl - рабочий нейтрал
  textDisabled: '#6e7681',
  green: '#1f8438',             // Terminal Green - единственный акцент (CTA)
  greenHover: '#26a144',
  phosphor: '#5fed83',          // зелёный для статусов/текста, не для заливок CTA
  sky: '#8dd6ff',               // ссылки, вторичный текстовый акцент
  violet: '#8c93fb',            // Ultraviolet - ТОЛЬКО бордер выделенной карточки
  red: '#f85149',
  amber: '#d29922',
};

const palette = {
  mode: 'dark',
  primary:   { main: T.green, light: T.greenHover, dark: '#166f2e', contrastText: '#ffffff' },
  secondary: { main: T.sky, contrastText: T.canvas },
  success:   { main: T.phosphor },
  warning:   { main: T.amber },
  error:     { main: T.red },
  info:      { main: T.sky },
  background:{ default: T.canvas, paper: T.surface },
  text:      { primary: T.textPrimary, secondary: T.textSecondary, disabled: T.textDisabled },
  divider:   T.border,
};

export function createAppTheme() {
  return createTheme({
    palette,
    shape: { borderRadius: 6 }, // база GitHub: 6px у кнопок/инпутов
    typography: {
      fontFamily: '"Inter", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      // Заголовки: Inter (substitute Mona Sans), вес ниже жирного, тесный трекинг
      h4: { fontWeight: 600, letterSpacing: '-0.03em' },
      h5: { fontWeight: 600, letterSpacing: '-0.025em' },
      h6: { fontWeight: 600, letterSpacing: '-0.02em' },
      subtitle2: { fontWeight: 600 },
      button: { fontWeight: 500 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: palette.background.default, color: palette.text.primary },
          '*::-webkit-scrollbar': { width: 10, height: 10 },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255,255,255,0.12)', borderRadius: 8,
            border: '2px solid transparent', backgroundClip: 'content-box',
          },
          '*::-webkit-scrollbar-thumb:hover': { backgroundColor: 'rgba(255,255,255,0.22)' },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          // По умолчанию НЕПРОЗРАЧНАЯ поверхность (меню, диалоги, поповеры) -
          // иначе выпадающие списки просвечивают. Glass-эффект только у карточек (ui.js).
          root: {
            backgroundImage: 'none',
            backgroundColor: T.surface,
            border: `1px solid ${T.glassBorder}`,
            boxShadow: 'none',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: '#1c2128',
            border: `1px solid ${T.borderStrong}`,
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
            borderRadius: 10,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: '#161b22',
            border: `1px solid ${T.borderStrong}`,
            boxShadow: '0 16px 48px rgba(0,0,0,0.55)',
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            backgroundColor: '#1c2128',
            border: `1px solid ${T.borderStrong}`,
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: 14,
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
            '&.Mui-selected': { backgroundColor: 'rgba(255,255,255,0.08)' },
            '&.Mui-selected:hover': { backgroundColor: 'rgba(255,255,255,0.10)' },
          },
        },
      },
      MuiButton: {
        disableElevation: true,
        styleOverrides: {
          root: { textTransform: 'none', borderRadius: 6, fontWeight: 500, paddingInline: 18 },
          containedPrimary: {
            backgroundColor: T.green, color: '#ffffff',
            '&:hover': { backgroundColor: T.greenHover },
          },
          outlined: {
            borderColor: T.borderStrong, color: T.textPrimary,
            '&:hover': { borderColor: T.textSecondary, backgroundColor: 'rgba(255,255,255,0.06)' },
          },
          text: { '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' } },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            backgroundColor: T.surface,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: T.border },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.borderStrong },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.sky, borderWidth: 1 },
          },
          input: { '&::placeholder': { color: T.textSecondary, opacity: 0.6 } },
        },
      },
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiChip: {
        styleOverrides: {
          // пилюли - вторая форма в системе (pill vocabulary)
          root: { borderRadius: 9999, fontWeight: 500 },
          outlined: { borderColor: T.borderStrong },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: T.border },
          head: { color: palette.text.secondary },
        },
      },
      MuiDivider: { styleOverrides: { root: { borderColor: T.border } } },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: 12, borderRadius: 6, backgroundColor: '#1c2128',
            border: `1px solid ${T.borderStrong}`, color: palette.text.primary,
          },
        },
      },
    },
  });
}
